import { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Cable, PauseCircle, PlayCircle, Ban } from 'lucide-react';
import EmptyView from '@/shared/components/EmptyView';
import { Badge } from '@/shared/ui/badge';
import ContainerLayout from '@/shared/components/ContainerLayout';
import { ServiceLineDetailsSection } from '@/domains/service-lines/components/ServiceLineDetailsSection';
import { CustomerSection } from '@/domains/service-lines/components/CustomerSection';
import { DevicesSection } from '@/domains/service-lines/components/DevicesSection';
import { ServiceLineUsageSection } from '@/domains/service-lines/components/ServiceLineUsageSection';
import { Button } from '@/shared/ui/button';
import { ServiceLineStatus } from '@/shared/types/serviceLineTypes';
import { getServiceLineStatusBGColorClass, SERVICE_LINE_STATUS_VALUE } from '@/shared/utils/color-utils-service-lines';
import GenericAlertDialog from '@/shared/components/GenericAlertDialog';
import { formatDateToLocaleString } from '@/shared/utils/date-utils';
import { useServiceLine, useDeactivateServiceLine, useResumeServiceLine } from '@/domains/service-lines/services/account-service-lines';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showSuccess, showErrorWithApiError } from '@/shared/utils/toast-utils';
import { usePermissions } from '@/shared/hooks/use-permissions';
import { Feature, Permission } from '@/shared/types/permissionsTypes';
import { type AppShellMode } from '@/app/shared/appShellMode';
import { useUser } from '@/contexts/UserContext';
import { OrgType } from '@/shared/types/userRoleTypes';
import { ServiceLinePauseDialog } from '../dialogs/ServiceLinePauseDialog';

export type ServiceLineDetailsPageProps = {
  mode: AppShellMode;
};

export const ServiceLineDetailsPage = ({ mode }: ServiceLineDetailsPageProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const serviceLineNumber = location.state?.serviceLineNumber;
  const { hasPermission } = usePermissions();
  const { selectedOrganization } = useUser();

  const {
    data: serviceLine,
    isLoading,
    refetch: refetchServiceLine,
    error,
  } = useServiceLine(serviceLineNumber);

  const [isPauseServiceLineDialogOpen, setIsPauseServiceLineDialogOpen] = useState(false);

  const [isResumeServiceLineDialogOpen, setIsResumeServiceLineDialogOpen] = useState(false);
  const resumeServiceLine = useResumeServiceLine();

  const [isDeactivateServiceLineDialogOpen, setIsDeactivateServiceLineDialogOpen] = useState(false);
  const deactivateServiceLine = useDeactivateServiceLine();

  const organizationName = useMemo(() => {
    return location.state?.customer?.name || selectedOrganization?.name || undefined;
  }, [selectedOrganization, location.state?.customer?.name]);

  const handleBackNavigation = () => {
    navigate(-1);
  };

  // Format the activation and deactivation dates
  const activationDate = serviceLine?.startDate
    ? formatDateToLocaleString(serviceLine.startDate)
    : null;

  // Format the deactivation date
  const deactivationDate = serviceLine?.endDate
    ? formatDateToLocaleString(serviceLine.endDate)
    : null;

  // Format the paused date
  const pausedAt = serviceLine?.pausedAt
    ? formatDateToLocaleString(serviceLine.pausedAt)
    : undefined;

  // Format the resumption date
  const resumesAt = serviceLine?.resumesAt
    ? formatDateToLocaleString(serviceLine.resumesAt, { year: 'numeric', month: 'short', day: 'numeric' })
    : undefined;
  /**
  * Determine if the service line can be deactivated
  * - The user must have the delete permission
  * - The service line must be active
  * - The service line must have no terminals
  * - The service line must be updateable (updateAllowed is true)
  */
  const canDeactivate =
    hasPermission(OrgType.TENANT, Feature.SERVICE_LINE_DEACTIVATION, Permission.MANAGE) &&
    serviceLine?.status && serviceLine.status !== ServiceLineStatus.SUSPENDED &&
    (serviceLine.terminals?.length ?? 0) === 0 &&
    serviceLine.updateAllowed === true;

  /**
  * Determine if the service line can be paused
  * - The user must have the manage permission
  * - The service line must be active
  * - The service line must have a Terminal
  * - The service line must be updateable (updateAllowed is true)
  */
  const canPause =
    hasPermission(OrgType.TENANT, Feature.SERVICE_LINE, Permission.MANAGE) &&
    serviceLine?.status &&
    serviceLine.status !== ServiceLineStatus.SUSPENDED &&
    serviceLine.status !== ServiceLineStatus.PAUSED &&
    serviceLine.status !== ServiceLineStatus.NO_UT &&
    serviceLine?.terminals?.length && serviceLine.terminals?.length > 0 &&
    serviceLine.updateAllowed === true;

  /**
   * Determine if the service line can be resumed
   * - The user must have the manage permission
   * - The service line must be paused
   * - The service line must be updateable (updateAllowed is true)
   */
  const canResume =
    hasPermission(OrgType.TENANT, Feature.SERVICE_LINE, Permission.MANAGE) &&
    serviceLine?.status && serviceLine.status === ServiceLineStatus.PAUSED &&
    serviceLine.updateAllowed === true;

  const serviceLineActionButton = useMemo(() => {
    if (canPause) {
      return <Button
        variant="default"
        size="sm"
        className="mx-auto gap-2"
        onClick={() => setIsPauseServiceLineDialogOpen(true)}
      >
        <PauseCircle className="h-4 w-4" />
        Pause
      </Button>
    }
    if (canResume) {
      return <Button
        variant="default"
        size="sm"
        className="mx-auto gap-2"
        onClick={() => setIsResumeServiceLineDialogOpen(true)}
      >
        <PlayCircle className="h-4 w-4" />
        Resume
      </Button>
    }
    if (canDeactivate) {
      return <Button
        variant="destructive"
        size="sm"
        className="mx-auto gap-2"
        onClick={() => setIsDeactivateServiceLineDialogOpen(true)}
      >
        <Ban className="h-4 w-4" />
        Suspend
      </Button>
    }
    return null;
  }, [canDeactivate, canPause, canResume]);

  // If no service line number is provided, show an empty view
  if (!serviceLineNumber) {
    return null;
  }

  // Confirm the deactivation of the service line
  const confirmDeactivateServiceLine = async () => {
    try {
      await deactivateServiceLine.mutateAsync({
        serviceLineNumber: serviceLine.serviceLineNumber,
      });
      showSuccess(SUCCESS.SERVICE_LINE.DEACTIVATED);
      refetchServiceLine();
      setIsDeactivateServiceLineDialogOpen(false);
    } catch (err) {
      showErrorWithApiError(err, ERROR.SERVICE_LINE.DEACTIVATE);
    }
  };

  // Confirm the resume of the service line
  const confirmResumeServiceLine = async () => {
    try {
      await resumeServiceLine.mutateAsync({
        serviceLineNumber: serviceLine.serviceLineNumber,
      });
      showSuccess(SUCCESS.SERVICE_LINE.RESUMED);
      refetchServiceLine();
      setIsResumeServiceLineDialogOpen(false);
    } catch (err) {
      showErrorWithApiError(err, ERROR.SERVICE_LINE.RESUME);
    }
  };

  return (
    <ContainerLayout shouldShowSidebar={false} shouldAllowRegionChange={false} shouldAllowAccountChange={false}>
      <div className="flex flex-col flex-1">
        <div className="glass-panel flex h-16 flex-row items-center shadow-sm">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackNavigation}
            className="hover:bg-transparent"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          {organizationName && <h2 className="flex-1 px-4 text-end text-2xl font-bold">{organizationName}</h2>}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-4 gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm">Loading service line details...</p>
          </div>
        ) : (
          error ? (
            <div className="flex items-center justify-center p-4">
              <EmptyView
                icon={Cable}
                title="Service Line Not Found"
                description="The service line you are looking for does not exist."
              />
            </div>
          ) : (
            <div className="flex flex-col flex-1 space-y-4 p-4">
              <div className="flex-1 overflow-y-auto space-y-4">
                <div className="flex flex-row items-center justify-between">
                  <div className="flex flex-col text-left">
                    <h1 className="text-3xl font-bold">{serviceLineNumber}</h1>
                    {activationDate && (
                      <p>
                        Activation Date:{' '}
                        <span className="font-bold text-green-500">{activationDate}</span>
                      </p>
                    )}
                    {deactivationDate && (
                      <p>
                        Deactivation Date:{' '}
                        <span className="font-bold text-red-500">{deactivationDate}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="flex flex-row items-center gap-2">
                      {serviceLine.status && (
                        <Badge
                          variant="default"
                          className={getServiceLineStatusBGColorClass(serviceLine.status)}
                        >
                          <p className="text-sm font-medium">
                            {SERVICE_LINE_STATUS_VALUE[serviceLine.status]}
                          </p>
                        </Badge>
                      )}
                      {serviceLineActionButton}
                    </div>
                    {pausedAt && (
                      <p>
                        Paused at:{' '}
                        <span className="font-bold text-gray-500">{pausedAt}</span>
                      </p>
                    )}
                    {resumesAt && (
                      <p>
                        Resumes on:{' '}
                        <span className="font-bold text-blue-500">{resumesAt}</span>
                      </p>
                    )}
                  </div>
                </div>

                <ServiceLineDetailsSection
                  key={`details-${serviceLine.serviceLineNumber}`}
                  mode={mode}
                  serviceLine={serviceLine}
                  organization={selectedOrganization}
                  refetchServiceLine={async () => {
                    await refetchServiceLine();
                  }}
                />
              </div>

              <ServiceLineUsageSection
                key={`usage-${serviceLine.serviceLineNumber}`}
                serviceLine={serviceLine}
                dataUsages={serviceLine.dataUsages || []}
              />

              <DevicesSection
                key={`devices-${serviceLine.serviceLineNumber}-${serviceLine.terminals?.length ?? 0}`}
                serviceLine={serviceLine}
                refetchServiceLine={async () => {
                  await refetchServiceLine();
                }}
              />

              {hasPermission(OrgType.TENANT, Feature.CUSTOMERS, Permission.MANAGE) && (
                <CustomerSection
                  key={`customer-${serviceLine.serviceLineNumber}-${serviceLine.customer?.customerId ?? 'none'}`}
                  serviceLine={serviceLine}
                  refetchServiceLine={async () => {
                    await refetchServiceLine();
                  }}
                />
              )}
            </div>
          )
        )}

        <GenericAlertDialog
          open={isDeactivateServiceLineDialogOpen}
          onOpenChange={setIsDeactivateServiceLineDialogOpen}
          title="Deactivate Service Line"
          description="Are you sure you want to deactivate this service line?"
          confirmLabel="Deactivate"
          cancelLabel="Cancel"
          onConfirm={confirmDeactivateServiceLine}
          loading={deactivateServiceLine.isPending}
        />

        <GenericAlertDialog
          open={isResumeServiceLineDialogOpen}
          onOpenChange={setIsResumeServiceLineDialogOpen}
          title="Resume Service Line"
          description="Are you sure you want to resume this service line?"
          confirmLabel="Resume"
          cancelLabel="Cancel"
          onConfirm={confirmResumeServiceLine}
          loading={resumeServiceLine.isPending}
        />

        <ServiceLinePauseDialog
          open={isPauseServiceLineDialogOpen}
          onOpenChange={setIsPauseServiceLineDialogOpen}
          serviceLine={serviceLine}
          onSuccess={() => {
            refetchServiceLine();
            setIsPauseServiceLineDialogOpen(false);
          }}
        />
      </div>
    </ContainerLayout >
  );
};

export default ServiceLineDetailsPage;
